import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import type {
  FeedbackCampaign,
  FeedbackCampaignFirestore,
  FeedbackQuestion,
  FeedbackResponse,
  FeedbackResponseFirestore,
} from '../../../types/dynamic-feedback';

/**
 * Service for managing feedback campaigns in admin interface
 */
export class FeedbackCampaignsService {
  /**
   * Get all feedback campaigns
   */
  async getAllCampaigns(): Promise<FeedbackCampaign[]> {
    try {
      const campaignsRef = collection(db, 'feedback_campaigns');
      const q = query(campaignsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const campaigns: FeedbackCampaign[] = [];

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data() as FeedbackCampaignFirestore;
        const questions = await this.getQuestions(docSnap.id);

        campaigns.push({
          id: docSnap.id,
          name: data.name,
          description: data.description,
          status: data.status,
          targetLocations: data.targetLocations,
          questions,
          cooldownConfig: data.cooldownConfig,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        });
      }

      return campaigns;
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  }

  /**
   * Get a single campaign by ID
   */
  async getCampaign(campaignId: string): Promise<FeedbackCampaign | null> {
    try {
      const docRef = doc(db, 'feedback_campaigns', campaignId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      const data = docSnap.data() as FeedbackCampaignFirestore;
      const questions = await this.getQuestions(campaignId);

      return {
        id: docSnap.id,
        name: data.name,
        description: data.description,
        status: data.status,
        targetLocations: data.targetLocations,
        questions,
        cooldownConfig: data.cooldownConfig,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
      };
    } catch (error) {
      console.error('Error fetching campaign:', error);
      throw error;
    }
  }

  /**
   * Create a new campaign
   */
  async createCampaign(
    campaign: Omit<FeedbackCampaign, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const campaignData = {
        name: campaign.name,
        description: campaign.description,
        status: campaign.status,
        targetLocations: campaign.targetLocations,
        ...(campaign.cooldownConfig && { cooldownConfig: campaign.cooldownConfig }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docRef = await addDoc(collection(db, 'feedback_campaigns'), campaignData);

      // Add questions
      await this.updateQuestions(docRef.id, campaign.questions);

      return docRef.id;
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }

  /**
   * Update an existing campaign
   */
  async updateCampaign(campaignId: string, updates: Partial<FeedbackCampaign>): Promise<void> {
    try {
      const docRef = doc(db, 'feedback_campaigns', campaignId);

      const updateData = {
        updatedAt: serverTimestamp(),
      } as Record<string, unknown>;

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.targetLocations !== undefined) updateData.targetLocations = updates.targetLocations;
      if (updates.cooldownConfig !== undefined) updateData.cooldownConfig = updates.cooldownConfig;

      await setDoc(docRef, updateData, { merge: true });

      // Update questions if provided
      if (updates.questions !== undefined) {
        await this.updateQuestions(campaignId, updates.questions);
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  }

  /**
   * Delete a campaign and its questions
   */
  async deleteCampaign(campaignId: string): Promise<void> {
    try {
      // Delete all questions first
      const questionsRef = collection(db, 'feedback_campaigns', campaignId, 'questions');
      const questionsSnapshot = await getDocs(questionsRef);

      const deletePromises = questionsSnapshot.docs.map((doc) => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Delete campaign
      await deleteDoc(doc(db, 'feedback_campaigns', campaignId));
    } catch (error) {
      console.error('Error deleting campaign:', error);
      throw error;
    }
  }

  /**
   * Get questions for a campaign
   */
  async getQuestions(campaignId: string): Promise<FeedbackQuestion[]> {
    try {
      const questionsRef = collection(db, 'feedback_campaigns', campaignId, 'questions');
      const q = query(questionsRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as FeedbackQuestion[];
    } catch (error) {
      console.error('Error fetching questions:', error);
      throw error;
    }
  }

  /**
   * Update questions for a campaign (smart update: updates existing, creates new, deletes removed)
   */
  async updateQuestions(campaignId: string, questions: FeedbackQuestion[]): Promise<void> {
    try {
      const questionsRef = collection(db, 'feedback_campaigns', campaignId, 'questions');

      // Get existing questions to determine what needs to be deleted
      const existingSnapshot = await getDocs(questionsRef);
      const existingIds = new Set(existingSnapshot.docs.map((doc) => doc.id));
      const incomingIds = new Set(questions.filter((q) => q.id).map((q) => q.id!));

      // Track operations
      const operations: Promise<unknown>[] = [];

      // Process each question
      questions.forEach((question) => {
        const questionData = {
          order: question.order,
          type: question.type,
          question: question.question,
          required: question.required,
          ...(question.config && { config: question.config }),
        };

        if (question.id) {
          // UPDATE existing question using setDoc
          const docRef = doc(questionsRef, question.id);
          operations.push(setDoc(docRef, questionData));
        } else {
          // CREATE new question using addDoc
          operations.push(addDoc(questionsRef, questionData));
        }
      });

      // DELETE questions that were removed
      existingIds.forEach((id) => {
        if (!incomingIds.has(id)) {
          operations.push(deleteDoc(doc(questionsRef, id)));
        }
      });

      await Promise.all(operations);
    } catch (error) {
      console.error('Error updating questions:', error);
      throw error;
    }
  }

  /**
   * Get responses for a campaign
   */
  async getResponses(campaignId: string): Promise<FeedbackResponse[]> {
    try {
      const responsesRef = collection(db, 'feedback_responses');
      const q = query(
        responsesRef,
        where('campaignId', '==', campaignId),
        orderBy('submittedAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data() as FeedbackResponseFirestore;
        return {
          id: doc.id,
          userId: data.userId,
          userEmail: data.userEmail,
          campaignId: data.campaignId,
          campaignName: data.campaignName,
          location: data.location,
          answers: data.answers,
          metadata: data.metadata,
          completedAt: data.completedAt.toDate(),
          submittedAt: data.submittedAt.toDate(),
        };
      });
    } catch (error) {
      console.error('Error fetching responses:', error);
      throw error;
    }
  }

  /**
   * Get response count for a campaign
   */
  async getResponseCount(campaignId: string): Promise<number> {
    try {
      const responsesRef = collection(db, 'feedback_responses');
      const q = query(responsesRef, where('campaignId', '==', campaignId));
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error fetching response count:', error);
      return 0;
    }
  }
}

export const feedbackCampaignsService = new FeedbackCampaignsService();
